import React, { useEffect, useState } from "react";
import { Gift, Send, Coins, Target, Download } from "lucide-react";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth.jsx";
import { Avatar, PageCard, PillBtn, DateRange, SearchBox, EmptyState, MemberPicker, Modal, defaultRange } from "./common";

const fmt = (d) => new Date(d).toLocaleDateString([], { day: "numeric", month: "short", year: "numeric" });

export default function RewardsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [range, setRange] = useState(defaultRange(30));
  const [data, setData] = useState(null);
  const [tab, setTab] = useState(isAdmin ? "sent" : "mine");
  const [q, setQ] = useState("");
  const [sendOpen, setSendOpen] = useState(false);

  const load = () => api.get("/rewards", { params: range }).then((r) => setData(r.data)).catch(() => toast.error("Could not load rewards"));
  useEffect(() => { load(); }, [range.start, range.end]);

  const rows = (data?.tokens || []).filter((r) => `${r.recipient_name} ${r.message} ${r.sender_name}`.toLowerCase().includes(q.toLowerCase()));
  const exportCsv = () => {
    const csv = ["Sent to,Amount,Message,Sent on,Sent by", ...rows.map((r) => [r.recipient_name, r.amount, `"${(r.message || "").replace(/"/g, "'")}"`, r.created_at.slice(0, 10), r.sender_name].join(","))].join("\n");
    const a = document.createElement("a"); a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" })); a.download = "rewards.csv"; a.click();
  };
  const tabs = isAdmin ? [["sent", "Sent tokens"], ["activity", "Users activity"], ["milestones", "Milestone rewards"]] : [["mine", "My rewards"], ["milestones", "Milestones"]];

  return (
    <Layout>
      <div className="p-4 lg:p-6 max-w-6xl mx-auto">
        <PageCard Icon={Gift} title="Rewards" testid="rewards-page" actions={isAdmin && <PillBtn primary onClick={() => setSendOpen(true)} data-testid="rewards-send-btn"><Send className="w-4 h-4" /> Send tokens</PillBtn>} />
        <div className="bg-white rounded-3xl border border-slate-100 shadow-sm">
          <div className="flex flex-wrap items-center gap-4 px-6 border-b border-slate-100">
            {tabs.map(([k, l]) => (
              <button key={k} onClick={() => setTab(k)} data-testid={`rewards-tab-${k}`} className={`py-4 text-[15px] font-semibold border-b-2 transition-colors ${tab === k ? "border-sky-600 text-sky-700" : "border-transparent text-slate-600 hover:text-slate-900"}`}>{l}</button>
            ))}
            {data && <span className="ml-auto my-3 flex items-center gap-2 bg-amber-50 text-amber-700 font-semibold rounded-2xl px-4 py-2" data-testid="rewards-balance">
              <Coins className="w-5 h-5 text-amber-500" /> {isAdmin ? "Tokens in period" : "My tokens (all time)"} <span className="font-display font-black text-xl">{isAdmin ? rows.reduce((t, r) => t + r.amount, 0) : data.my_all_time_tokens}</span>
            </span>}
          </div>
          <div className="p-6">
            <div className="flex flex-wrap items-center gap-3 mb-5">
              {tab !== "milestones" && <SearchBox value={q} onChange={setQ} testid="rewards-search" />}
              <DateRange value={range} onChange={setRange} testid="rewards-range" />
              {isAdmin && tab === "sent" && <button onClick={exportCsv} className="ml-auto w-11 h-11 rounded-full border border-slate-200 flex items-center justify-center text-sky-600 hover:bg-sky-50" title="Export CSV" data-testid="rewards-export"><Download className="w-4 h-4" /></button>}
            </div>
            {!data ? <div className="py-12 text-center text-slate-400">Loading…</div>
              : tab === "activity" ? <Activity totals={data.totals} />
              : tab === "milestones" ? <Milestones data={data} isAdmin={isAdmin} />
              : <TokensTable rows={rows} isAdmin={isAdmin} onSend={() => setSendOpen(true)} />}
          </div>
        </div>
      </div>
      {sendOpen && <SendTokensModal onClose={() => setSendOpen(false)} onSent={() => { setSendOpen(false); setData(null); load(); }} />}
    </Layout>
  );
}

function TokensTable({ rows, isAdmin, onSend }) {
  if (!rows.length) return <EmptyState Icon={Gift} title={isAdmin ? "Sent tokens will show here" : "No rewards in this period"} text={isAdmin ? "Once you send tokens to your users you will see this activity here." : "Pick an earlier date range to see older rewards."} action={isAdmin && <PillBtn primary onClick={onSend}>Send tokens</PillBtn>} />;
  return (
    <div className="overflow-x-auto"><table className="w-full text-sm" data-testid="rewards-table">
      <thead><tr className="bg-slate-50 text-slate-600 text-left">{(isAdmin ? ["Sent to", "Amount", "Message", "Sent on", "Sent by"] : ["Amount", "Message", "Received on", "From"]).map((h) => <th key={h} className="px-4 py-3 font-semibold first:rounded-l-xl last:rounded-r-xl">{h}</th>)}</tr></thead>
      <tbody>{rows.map((r) => (
        <tr key={r.token_id} className="border-b border-slate-50" data-testid={`reward-row-${r.token_id}`}>
          {isAdmin && <td className="px-4 py-3"><span className="flex items-center gap-2"><Avatar name={r.recipient_name} photo={r.recipient_photo} size="w-8 h-8" />{r.recipient_name}</span></td>}
          <td className="px-4 py-3 font-bold text-amber-600"><Coins className="w-4 h-4 inline mr-1" />{r.amount}</td>
          <td className="px-4 py-3 text-slate-600">{r.message || "—"}{r.kind === "milestone" && <span className="ml-2 text-[10px] font-bold bg-violet-100 text-violet-700 rounded-full px-2 py-0.5">MILESTONE</span>}</td>
          <td className="px-4 py-3 text-slate-500">{fmt(r.created_at)}</td>
          <td className="px-4 py-3 text-slate-500">{r.sender_name}</td>
        </tr>
      ))}</tbody>
    </table></div>
  );
}

const Activity = ({ totals }) => (!totals.length ? <EmptyState Icon={Coins} title="No activity in this period" text="Tokens sent in the selected dates appear here per member." /> : (
  <div className="divide-y divide-slate-50" data-testid="rewards-activity">{totals.map((t, i) => (
    <div key={t.user_id} className="flex items-center gap-4 py-3"><span className="w-6 text-slate-400 font-bold">{i + 1}</span><Avatar name={t.name} /><span className="flex-1 font-semibold">{t.name}</span><span className="text-sm text-slate-500">{t.count} reward{t.count === 1 ? "" : "s"}</span><span className="font-bold text-amber-600 w-20 text-right">{t.tokens} tokens</span></div>
  ))}</div>
));

function Milestones({ data, isAdmin }) {
  const nxt = data.next_milestone;
  return (
    <div data-testid="rewards-milestones">
      {!isAdmin && <div className="rounded-2xl bg-gradient-to-r from-violet-50 to-sky-50 p-5 mb-5" data-testid="rewards-next-milestone">
        <div className="flex items-center gap-2 font-bold"><Target className="w-5 h-5 text-violet-600" /> Your XP: {data.my_xp}</div>
        {nxt ? <>
          <div className="text-sm text-slate-600 mt-1">Next: <b>{nxt.title}</b> at {nxt.xp} XP{nxt.tokens ? ` (+${nxt.tokens} tokens)` : ""}</div>
          <div className="h-2.5 bg-white rounded-full mt-3 overflow-hidden"><div className="h-full bg-gradient-to-r from-violet-500 to-sky-500" style={{ width: `${Math.min(100, (data.my_xp / nxt.xp) * 100)}%` }} /></div>
        </> : <div className="text-sm text-emerald-700 mt-1">You've unlocked every milestone!</div>}
      </div>}
      <div className="grid sm:grid-cols-3 gap-3 mb-6">{data.milestones.map((m) => (
        <div key={m.xp} className="border border-slate-100 rounded-2xl p-4"><div className="text-xs text-slate-500">{m.xp} XP</div><div className="font-bold">{m.title}</div><div className="text-sm text-amber-600">+{m.tokens} tokens</div></div>
      ))}</div>
      <h4 className="font-bold mb-2">{isAdmin ? "Unlocked by members" : "Unlocked"}</h4>
      {data.milestones_earned.length === 0 ? <div className="text-sm text-slate-400">None yet.</div> : data.milestones_earned.map((m) => (
        <div key={m.reward_id} className="flex items-center gap-3 py-2 border-b border-slate-50 text-sm"><Gift className="w-4 h-4 text-violet-500" />{isAdmin && <b>{m.name}</b>}<span>{m.title} ({m.xp} XP)</span><span className="ml-auto text-slate-400">{fmt(m.created_at)}</span></div>
      ))}
    </div>
  );
}

function SendTokensModal({ onClose, onSent }) {
  const [picked, setPicked] = useState([]);
  const [amount, setAmount] = useState("10");
  const [message, setMessage] = useState("");
  const send = async () => {
    try { const r = await api.post("/rewards/send", { recipient_ids: picked, amount: parseInt(amount, 10), message }); toast.success(`Sent to ${r.data.sent} member(s) — they've been emailed`); onSent(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <Modal title="Send tokens" onClose={onClose} testid="send-tokens-modal">
      <MemberPicker picked={picked} setPicked={setPicked} />
      <input type="number" min="1" value={amount} onChange={(e) => setAmount(e.target.value)} className="input-pill w-full mt-3" placeholder="Tokens" data-testid="send-tokens-amount" />
      <textarea value={message} onChange={(e) => setMessage(e.target.value)} className="input-pill w-full mt-2 min-h-[80px]" placeholder="Message (why they're being rewarded)" data-testid="send-tokens-message" />
      <div className="mt-4"><PillBtn primary disabled={!picked.length} onClick={send} data-testid="send-tokens-submit"><Send className="w-4 h-4" /> Send to {picked.length || "…"}</PillBtn></div>
    </Modal>
  );
}
